import AppButton from '@nxsys/components/AppButton';

import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useSendTestEmail, useUpdateCompanyById } from '@nxsys/hooks/api/company';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { showMessage } from '@nxsys/utils/message';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { useCallback, useMemo, useRef, useState } from 'react';
import EmailSettingsForm, {
  EmailSettingsFormRef,
  EmailSettingsFormValues,
  EmailSettingType,
} from './components/EmailSettingsForm';
import EmailTemplates from './components/EmailTemplates';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

enum EmailTabEnum {
  EMAIL_SETTINGS = 'EMAIL_SETTINGS',
  EMAIL_TEMPLATES = 'EMAIL_TEMPLATES',
}

const EmailTab: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const formRef = useRef<EmailSettingsFormRef>(null);
  const { run: updateCompany, loading: isUpdatingCompany } =
    useUpdateCompanyById();
  const { run: sendTestEmail, loading: isSendingTestEmail } =
    useSendTestEmail();
  const tabs = useMemo(
    () => [
      {
        tab: messages['company.emailSettings.tabTitle'],
        key: EmailTabEnum.EMAIL_SETTINGS,
      },
      {
        tab: messages['company.emailTemplates.tabTitle'],
        key: EmailTabEnum.EMAIL_TEMPLATES,
      },
    ],
    [messages],
  );

  const [activeTab, setActiveTab] = useState<EmailTabEnum>(
    EmailTabEnum.EMAIL_SETTINGS,
  );

  const handleTabChange = (key: EmailTabEnum) => {
    setActiveTab(key);
  };

  const handleSubmit = useCallback(
    async (values: EmailSettingsFormValues) => {
      if (!data?.id) return;

      const isDefault = values.emailSettingType === EmailSettingType.DEFAULT;

      const params: UpdateCompanyByIdParams = {
        isUseDefaultEmailSettings: isDefault,
        ...(isDefault
          ? {}
          : {
              emailSender: values.emailSender || '',
              smtpHost: values.smtpHost || '',
              smtpPort: values.smtpPort || '',
              smtpSecure: values.smtpSecure ?? false,
              smtpUser: values.smtpUser || '',
              smtpPassword: values.smtpPassword,
            }),
      };

      try {
        const res = await updateCompany(data.id, params);
        if (res.success) {
          showMessage({
            type: 'success',
            content: messages['common.updateSuccess'],
          });
        } else {
          // A rejected SMTP config comes back in-band with a 200.
          showMessage({
            type: 'error',
            content:
              res.message || messages['company.emailSettings.updateFailed'],
          });
        }
      } catch {
        // useUpdateCompanyById already toasts non-2xx errors.
      }
    },
    [data, updateCompany, messages],
  );

  const handleTestEmail = useCallback(async () => {
    if (!data?.id) return;

    // getValues() runs validateFields(), which rejects on invalid input. Its
    // inline errors on the fields are the feedback, so there is nothing to add.
    const values = await formRef.current?.getValues().catch(() => undefined);
    if (!values) return;

    try {
      const res = await sendTestEmail(data.id, {
        // Without this the test only checks the credentials, not the sender.
        emailSender: values.emailSender,
        smtpHost: values.smtpHost,
        smtpPort: values.smtpPort,
        smtpSecure: values.smtpSecure ?? false,
        smtpUser: values.smtpUser,
        smtpPassword: values.smtpPassword || '',
      });
      if (res?.success) {
        showMessage({
          type: 'success',
          content: messages['company.create.testEmailSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: res?.message || messages['company.create.testEmailFailed'],
        });
      }
    } catch (error) {
      showMessage({ type: 'error', content: getErrorMessage(error) });
    }
  }, [data, sendTestEmail, messages]);

  const [isDefaultEmail, setIsDefaultEmail] = useState(
    data?.isUseDefaultEmailSettings === true,
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case EmailTabEnum.EMAIL_SETTINGS:
        return (
          <EmailSettingsForm
            companyData={data}
            formRef={formRef}
            onSubmit={handleSubmit}
            onEmailSettingTypeChange={(type) =>
              setIsDefaultEmail(type === EmailSettingType.DEFAULT)
            }
          />
        );
      case EmailTabEnum.EMAIL_TEMPLATES:
        return <EmailTemplates data={data} />;
      default:
        return <></>;
    }
  };

  return (
    <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">{renderTabContent()}</div>
        {data && activeTab === EmailTabEnum.EMAIL_SETTINGS && (
          <div className="flex items-center justify-start gap-3 pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={() => formRef.current?.submit()}
              loading={isUpdatingCompany}
            />
            <AppButton
              type="default"
              name={messages['company.emailSettings.testEmail']}
              onClick={handleTestEmail}
              loading={isSendingTestEmail}
              disabled={isDefaultEmail}
            />
          </div>
        )}
    </AppCardTabListContainer>
  );
};

export default EmailTab;
